import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, PenLine, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AshokaChakra } from "@/components/AshokaChakra";
import { LangToggle } from "@/components/LangToggle";

const links = [
  { to: "/", label: "Home" },
  { to: "/articles", label: "Articles" },
  { to: "/#roots", label: "Root of India" },
  { to: "/#growth", label: "Growth" },
  { to: "/#geopolitics", label: "Geopolitics" },
];

export function Navbar({ hideBar = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="sticky top-0 z-40 border-b border-[var(--line)] tricolor-light-h backdrop-blur-md" data-testid="navbar">
      <div className={`h-1 w-full ${hideBar ? "bg-[var(--line)]" : "tricolor-bar"}`} />
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <AshokaChakra className="h-9 w-9 text-[var(--chakra)] chakra-spin" />
          <span className="leading-none">
            <span className="block font-devanagari text-2xl text-[var(--chakra-deep)]">भारत गाथा</span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-[var(--saffron-deep)]">Bharat Gatha</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-medium transition-colors hover:text-[var(--saffron-deep)] ${
                pathname === l.to ? "text-[var(--chakra)]" : "text-stone-600"
              }`}
            >
              {l.label}
            </NavLink>
          ))}
          <LangToggle />
          <Link
            to="/articles?q="
            data-testid="nav-search-link"
            aria-label="Search articles"
            className="rounded-full p-2 text-stone-600 transition-colors hover:bg-[var(--saffron-tint)] hover:text-[var(--saffron-deep)]"
          >
            <Search className="h-5 w-5" />
          </Link>
          <Link
            to={user ? "/admin" : "/login"}
            data-testid="nav-admin-link"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--chakra)] px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-[var(--chakra-deep)] hover:shadow-lg"
          >
            <PenLine className="h-4 w-4" /> {user ? "Dashboard" : "Author Login"}
          </Link>
        </nav>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-[var(--line)] tricolor-light-h px-5 py-4 md:hidden" data-testid="nav-mobile-menu">
          <div className="pb-3"><LangToggle /></div>
          <Link to="/articles?q=" onClick={() => setOpen(false)} data-testid="nav-mobile-search-link" className="flex items-center gap-2 py-2 text-base font-medium text-stone-700">
            <Search className="h-4 w-4" /> Search articles
          </Link>
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-base font-medium text-stone-700">
              {l.label}
            </Link>
          ))}
          <Link to={user ? "/admin" : "/login"} onClick={() => setOpen(false)} className="mt-2 block rounded-full bg-[var(--chakra)] px-4 py-2 text-center font-semibold text-white">
            {user ? "Dashboard" : "Author Login"}
          </Link>
        </div>
      )}
    </header>
  );
}
